<template>
  <AdminLayout page-title="Amazon API Settings" page-subtitle="Configure Amazon Creators API">
    <div class="max-w-4xl mx-auto">
          <!-- Page Header -->
          <div class="mb-8">
            <div class="inline-flex items-center px-4 py-2 bg-emerald-500/10 border border-emerald-500/20 rounded-full text-emerald-400 text-sm font-medium mb-6">
              <span class="w-2 h-2 bg-emerald-400 rounded-full mr-2 animate-pulse"></span>
              Amazon Creators API
            </div>
            <h1 class="text-4xl font-bold text-white mb-4">Amazon Creators API</h1>
            <p class="text-xl text-slate-300 mb-8">Connect OilBuddy’s store to Amazon’s product catalog through the Associates <a href="https://affiliate-program.amazon.com/creatorsapi/docs/en-us/introduction" target="_blank" rel="noopener" class="text-emerald-400 hover:text-emerald-300 underline">Creators API</a>. Product Advertising API 5.0 is being replaced by this REST API.</p>
          </div>

          <!-- Configuration Status -->
          <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 mb-8">
            <h3 class="text-lg font-semibold text-white mb-4">Configuration Status</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div class="flex items-center space-x-3">
                <div :class="configStatus.clientId ? 'bg-emerald-500' : 'bg-red-500'" class="w-3 h-3 rounded-full"></div>
                <span class="text-slate-300">Client ID</span>
                <span :class="configStatus.clientId ? 'text-emerald-400' : 'text-red-400'" class="text-sm font-medium">
                  {{ configStatus.clientId ? 'Configured' : 'Missing' }}
                </span>
              </div>
              <div class="flex items-center space-x-3">
                <div :class="configStatus.clientSecret ? 'bg-emerald-500' : 'bg-red-500'" class="w-3 h-3 rounded-full"></div>
                <span class="text-slate-300">Client Secret</span>
                <span :class="configStatus.clientSecret ? 'text-emerald-400' : 'text-red-400'" class="text-sm font-medium">
                  {{ configStatus.clientSecret ? 'Configured' : 'Missing' }}
                </span>
              </div>
              <div class="flex items-center space-x-3">
                <div :class="configStatus.partnerTag ? 'bg-emerald-500' : 'bg-red-500'" class="w-3 h-3 rounded-full"></div>
                <span class="text-slate-300">Partner Tag</span>
                <span :class="configStatus.partnerTag ? 'text-emerald-400' : 'text-red-400'" class="text-sm font-medium">
                  {{ configStatus.partnerTag ? 'Configured' : 'Missing' }}
                </span>
              </div>
              <div class="flex items-center space-x-3">
                <div :class="configStatus.isConfigured ? 'bg-emerald-500' : 'bg-yellow-500'" class="w-3 h-3 rounded-full"></div>
                <span class="text-slate-300">Overall Status</span>
                <span :class="configStatus.isConfigured ? 'text-emerald-400' : 'text-yellow-400'" class="text-sm font-medium">
                  {{ configStatus.isConfigured ? 'Ready' : 'Incomplete' }}
                </span>
              </div>
            </div>
          </div>

          <!-- API Configuration Form -->
          <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6 mb-8">
            <h3 class="text-lg font-semibold text-white mb-6">API Credentials</h3>
            
            <form @submit.prevent="saveConfiguration" class="space-y-6">
              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="block text-sm font-medium text-slate-300 mb-2">Client ID</label>
                  <input 
                    v-model="credentials.clientId"
                    type="text"
                    placeholder="amzn1.application-oa2-client..."
                    class="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                    required
                  >
                  <p class="text-xs text-slate-400 mt-1">Login with Amazon client ID from Creators API credentials</p>
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-slate-300 mb-2">Client Secret</label>
                  <input 
                    v-model="credentials.clientSecret"
                    type="password"
                    placeholder="Client secret"
                    class="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                    required
                  >
                  <p class="text-xs text-slate-400 mt-1">Login with Amazon client secret. Used only on the server to request a bearer token.</p>
                </div>
              </div>

              <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label class="block text-sm font-medium text-slate-300 mb-2">Partner Tag</label>
                  <input 
                    v-model="credentials.partnerTag"
                    type="text"
                    placeholder="oilbuddy-20"
                    class="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                    required
                  >
                  <p class="text-xs text-slate-400 mt-1">Associates tracking ID for the selected marketplace</p>
                </div>
                
                <div>
                  <label class="block text-sm font-medium text-slate-300 mb-2">Marketplace</label>
                  <select 
                    v-model="credentials.marketplace"
                    class="w-full px-4 py-3 bg-slate-700/50 border border-slate-600/50 rounded-lg text-white focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500"
                  >
                    <option value="www.amazon.com">Amazon.com (US)</option>
                    <option value="www.amazon.ca">Amazon.ca (Canada)</option>
                    <option value="www.amazon.com.mx">Amazon.com.mx (Mexico)</option>
                    <option value="www.amazon.com.br">Amazon.com.br (Brazil)</option>
                    <option value="www.amazon.co.uk">Amazon.co.uk (UK)</option>
                    <option value="www.amazon.de">Amazon.de (Germany)</option>
                    <option value="www.amazon.fr">Amazon.fr (France)</option>
                    <option value="www.amazon.it">Amazon.it (Italy)</option>
                    <option value="www.amazon.es">Amazon.es (Spain)</option>
                    <option value="www.amazon.nl">Amazon.nl (Netherlands)</option>
                    <option value="www.amazon.in">Amazon.in (India)</option>
                    <option value="www.amazon.co.jp">Amazon.co.jp (Japan)</option>
                    <option value="www.amazon.com.au">Amazon.com.au (Australia)</option>
                    <option value="www.amazon.sg">Amazon.sg (Singapore)</option>
                  </select>
                  <p class="text-xs text-slate-400 mt-1">Sent as x-marketplace on Creators API requests</p>
                </div>
              </div>

              <div class="flex items-center justify-between pt-6 border-t border-slate-600/30">
                <button 
                  type="button"
                  @click="testConnection"
                  :disabled="!canTest || testing"
                  class="px-6 py-3 bg-blue-500 hover:bg-blue-600 disabled:bg-slate-600 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors flex items-center space-x-2"
                >
                  <svg v-if="testing" class="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                  </svg>
                  <span>{{ testing ? 'Testing...' : 'Test Connection' }}</span>
                </button>
                
                <button 
                  type="submit"
                  :disabled="saving"
                  class="px-6 py-3 bg-emerald-500 hover:bg-emerald-600 disabled:bg-slate-600 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-colors flex items-center space-x-2"
                >
                  <svg v-if="saving" class="w-5 h-5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                  </svg>
                  <span>{{ saving ? 'Saving...' : 'Save Configuration' }}</span>
                </button>
              </div>
            </form>
            <p v-if="actionMessage" class="mt-4 text-sm" :class="actionOk ? 'text-emerald-400' : 'text-red-400'">{{ actionMessage }}</p>
          </div>

          <!-- Setup Instructions -->
          <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 p-6">
            <h3 class="text-lg font-semibold text-white mb-4">Setup Instructions</h3>
            <div class="space-y-4 text-slate-300">
              <div class="flex items-start space-x-3">
                <span class="flex-shrink-0 w-6 h-6 bg-emerald-500/20 text-emerald-400 rounded-full flex items-center justify-center text-sm font-medium">1</span>
                <div>
                  <p class="font-medium">Join Amazon Associates</p>
                  <p class="text-sm text-slate-400">Enroll for the marketplace you want to sell in and keep a Partner Tag for that locale.</p>
                </div>
              </div>
              <div class="flex items-start space-x-3">
                <span class="flex-shrink-0 w-6 h-6 bg-emerald-500/20 text-emerald-400 rounded-full flex items-center justify-center text-sm font-medium">2</span>
                <div>
                  <p class="font-medium">Register for Creators API</p>
                  <p class="text-sm text-slate-400">Amazon requires at least 10 qualifying sales in the last 30 days, then you register in Associates Central and generate credentials.</p>
                </div>
              </div>
              <div class="flex items-start space-x-3">
                <span class="flex-shrink-0 w-6 h-6 bg-emerald-500/20 text-emerald-400 rounded-full flex items-center justify-center text-sm font-medium">3</span>
                <div>
                  <p class="font-medium">Copy Client ID and Client Secret</p>
                  <p class="text-slate-400 text-sm">Creators API uses Login with Amazon OAuth (scope creatorsapi::default), not PA-API access keys.</p>
                </div>
              </div>
              <div class="flex items-start space-x-3">
                <span class="flex-shrink-0 w-6 h-6 bg-emerald-500/20 text-emerald-400 rounded-full flex items-center justify-center text-sm font-medium">4</span>
                <div>
                  <p class="font-medium">Save and test</p>
                  <p class="text-sm text-slate-400">OilBuddy requests a bearer token on the server, then calls SearchItems / GetItems on https://creatorsapi.amazon.</p>
                </div>
              </div>
            </div>
            <p class="text-sm text-slate-400 mt-6">
              Docs:
              <a href="https://affiliate-program.amazon.com/creatorsapi/docs/en-us/introduction" target="_blank" rel="noopener" class="text-emerald-400 hover:text-emerald-300 underline">Introduction</a>
              ·
              <a href="https://affiliate-program.amazon.com/creatorsapi/docs/en-us/get-started/using-curl" target="_blank" rel="noopener" class="text-emerald-400 hover:text-emerald-300 underline">Using cURL</a>
            </p>
            
            <div class="mt-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
              <div class="flex items-start space-x-3">
                <svg class="w-5 h-5 text-blue-400 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
                </svg>
                <div>
                  <p class="text-blue-400 font-medium">Important Note</p>
                  <p class="text-sm text-blue-300">Credentials work across marketplaces, but you still need a valid Partner Tag and API approval in the locale you call. Cache the OAuth token until it expires. Never put the Client Secret in public code.</p>
                </div>
              </div>
            </div>
          </div>
    </div>
  </AdminLayout>
</template>


<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import AdminLayout from './AdminLayout.vue'
import { amazonService } from '../../services/amazonService'
import { buildApiUrl } from '../../config/api'

const saving = ref(false)
const testing = ref(false)
const actionMessage = ref('')
const actionOk = ref(false)
const secretOnServer = ref(false)

const credentials = ref({
  clientId: '',
  clientSecret: '',
  partnerTag: '',
  region: 'NA',
  marketplace: 'www.amazon.com'
})

const hasSecret = computed(() => {
  const secret = credentials.value.clientSecret.trim()
  return secret !== '' || secretOnServer.value
})

const configStatus = computed(() => {
  const clientId = credentials.value.clientId.trim() !== ''
  const clientSecret = hasSecret.value
  const partnerTag = credentials.value.partnerTag.trim() !== ''
  return {
    clientId,
    clientSecret,
    partnerTag,
    isConfigured: clientId && clientSecret && partnerTag
  }
})

const canTest = computed(() => configStatus.value.isConfigured)

const adminHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('adminToken') || ''
  return token
    ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }
    : { 'Content-Type': 'application/json' }
}

const payloadForSave = () => ({
  clientId: credentials.value.clientId,
  clientSecret: credentials.value.clientSecret === '••••••••' ? '' : credentials.value.clientSecret,
  partnerTag: credentials.value.partnerTag,
  marketplace: credentials.value.marketplace,
  region: credentials.value.region
})

const persistLocal = () => {
  localStorage.setItem('amazonApiConfig', JSON.stringify({
    clientId: credentials.value.clientId,
    partnerTag: credentials.value.partnerTag,
    marketplace: credentials.value.marketplace,
    region: credentials.value.region,
    hasClientSecret: hasSecret.value
  }))
}

const saveConfiguration = async () => {
  saving.value = true
  actionMessage.value = ''
  try {
    amazonService.initialize(credentials.value)
    amazonService.markSecretOnServer(hasSecret.value)
    persistLocal()
    const response = await fetch(buildApiUrl('admin/amazon'), {
      method: 'POST',
      headers: adminHeaders(),
      body: JSON.stringify(payloadForSave())
    })
    const data = await response.json().catch(() => ({}))
    if (!response.ok || data.success === false) {
      throw new Error(data.error || `Save failed (${response.status})`)
    }
    secretOnServer.value = true
    if (credentials.value.clientSecret && credentials.value.clientSecret !== '••••••••') {
      credentials.value.clientSecret = '••••••••'
    }
    amazonService.markSecretOnServer(true)
    persistLocal()
    actionOk.value = true
    actionMessage.value = 'Creators API credentials saved.'
    showNotification(actionMessage.value, 'success')
  } catch (error) {
    console.error('Save error:', error)
    actionOk.value = false
    actionMessage.value = error instanceof Error ? error.message : 'Failed to save configuration.'
    showNotification(actionMessage.value, 'error')
  } finally {
    saving.value = false
  }
}

const testConnection = async () => {
  testing.value = true
  actionMessage.value = ''
  try {
    amazonService.initialize(credentials.value)
    amazonService.markSecretOnServer(secretOnServer.value)
    const result = await amazonService.testConnection()
    actionOk.value = result.ok
    actionMessage.value = result.message || (result.ok ? 'Creators API token request succeeded.' : 'Creators API connection failed.')
    showNotification(actionMessage.value, result.ok ? 'success' : 'error')
  } catch (error) {
    console.error('Test error:', error)
    actionOk.value = false
    actionMessage.value = error instanceof Error ? error.message : 'API connection failed. Please check your credentials.'
    showNotification(actionMessage.value, 'error')
  } finally {
    testing.value = false
  }
}

const showNotification = (message: string, type: 'success' | 'error' | 'warning' = 'success') => {
  // Create notification element
  const notification = document.createElement('div')
  const bgColor = type === 'success' ? 'bg-emerald-500' : type === 'error' ? 'bg-red-500' : 'bg-yellow-500'
  notification.className = `fixed top-4 right-4 ${bgColor} text-white px-6 py-3 rounded-lg shadow-lg z-50 transform translate-x-full transition-transform duration-300`
  notification.textContent = message
  
  document.body.appendChild(notification)
  
  // Animate in
  setTimeout(() => {
    notification.classList.remove('translate-x-full')
  }, 100)
  
  // Remove after 3 seconds
  setTimeout(() => {
    notification.classList.add('translate-x-full')
    setTimeout(() => {
      document.body.removeChild(notification)
    }, 300)
  }, 3000)
}

// Lifecycle
onMounted(async () => {
  const savedConfig = localStorage.getItem('amazonApiConfig')
  if (savedConfig) {
    try {
      const config = JSON.parse(savedConfig)
      credentials.value = {
        clientId: config.clientId || config.apiKey || '',
        clientSecret: config.clientSecret || config.secretKey || '',
        partnerTag: config.partnerTag || config.associateTag || '',
        region: config.region || 'NA',
        marketplace: config.marketplace || 'www.amazon.com'
      }
      if (config.hasClientSecret && !credentials.value.clientSecret) {
        credentials.value.clientSecret = '••••••••'
        secretOnServer.value = true
      }
    } catch (error) {
      console.error('Failed to load saved configuration:', error)
    }
  }
  try {
    const response = await fetch(buildApiUrl('admin/amazon'), { headers: adminHeaders() })
    if (response.ok) {
      const data = await response.json()
      credentials.value = {
        clientId: data.clientId || credentials.value.clientId,
        clientSecret: data.hasClientSecret ? '••••••••' : credentials.value.clientSecret,
        partnerTag: data.partnerTag || credentials.value.partnerTag,
        region: data.region || credentials.value.region,
        marketplace: data.marketplace || credentials.value.marketplace
      }
      secretOnServer.value = !!data.hasClientSecret
      amazonService.markSecretOnServer(!!data.hasClientSecret)
      persistLocal()
    }
  } catch (error) {
    console.error('Failed to load Creators API settings:', error)
  }
  amazonService.initialize(credentials.value)
})
</script>

<style scoped>
/* Navigation Styles */
.nav-item {
  @apply flex items-center px-3 py-2 text-sm font-medium text-slate-300 rounded-lg transition-all duration-200 hover:text-white hover:bg-slate-700/50;
}

.nav-item.active {
  @apply text-emerald-400 bg-emerald-500/10 border border-emerald-500/20;
}

/* Animated Background */
.bg-grid-pattern {
  background-image: 
    linear-gradient(rgba(16, 185, 129, 0.1) 1px, transparent 1px),
    linear-gradient(90deg, rgba(16, 185, 129, 0.1) 1px, transparent 1px);
  background-size: 50px 50px;
}

/* Floating Particles */
.particle {
  position: absolute;
  border-radius: 50%;
  background: linear-gradient(45deg, #10b981, #06b6d4);
  animation: float 6s ease-in-out infinite;
}

.particle-1 {
  width: 4px;
  height: 4px;
  top: 20%;
  left: 10%;
  animation-delay: 0s;
}

.particle-2 {
  width: 6px;
  height: 6px;
  top: 60%;
  left: 80%;
  animation-delay: 2s;
}

.particle-3 {
  width: 3px;
  height: 3px;
  top: 40%;
  left: 60%;
  animation-delay: 4s;
}

.particle-4 {
  width: 5px;
  height: 5px;
  top: 80%;
  left: 20%;
  animation-delay: 1s;
}

.particle-5 {
  width: 4px;
  height: 4px;
  top: 30%;
  left: 90%;
  animation-delay: 3s;
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px) translateX(0px);
    opacity: 0.7;
  }
  50% {
    transform: translateY(-20px) translateX(10px);
    opacity: 1;
  }
}
</style>
